import { useToast } from "@crm-src/hooks/use-toast"
import { Box, Chip, Typography } from "@mui/material"
import { alpha } from "@mui/material/styles"
import { ConfirmDialog } from "@/components/ui/confirm-dialog"
import { leadsApi } from "@crm-src/lib/leads-api"
import type { DetailedLead } from "@crm-src/types/lead"
import { FinancialOnly, Money } from "@crm-src/components/money"

interface DeleteProspectModalProps {
  isOpen: boolean
  onClose: () => void
  prospect: DetailedLead | null
  onProspectDeleted?: (deletedProspectId: string) => void
  onNavigateToList?: () => void
}

type PriorityTone = "error" | "warning" | "info" | "neutral"

const PRIORITY_TONE: Record<string, PriorityTone> = {
  urgent: "error",
  critical: "error",
  high: "warning",
  medium: "info",
  low: "neutral",
}

export function DeleteProspectModal({
  isOpen,
  onClose,
  prospect,
  onProspectDeleted,
  onNavigateToList,
}: DeleteProspectModalProps) {
  const { toast } = useToast()

  if (!prospect) return null

  const handleDelete = async () => {
    try {
      await leadsApi.delete(prospect.id)
      toast({ title: "Success", description: "Prospect deleted successfully" })
      onProspectDeleted?.(prospect.id)
      onNavigateToList?.()
    } catch (error: any) {
      console.error("Error deleting prospect:", error)

      const errors: string[] | undefined = error?.response?.data?.errors
      const hasConstraint =
        errors?.some((err) => err.includes("foreign key constraint") || err.includes("still referenced"))

      if (hasConstraint) {
        toast({
          title: "Cannot Delete Prospect",
          description: "This prospect has associated records that must be deleted first.",
          variant: "destructive",
        })
      } else {
        toast({
          title: "Error",
          description: errors?.join(", ") || "Failed to delete prospect",
          variant: "destructive",
        })
      }
      throw error
    }
  }

  const priorityTone: PriorityTone = prospect.priority
    ? PRIORITY_TONE[prospect.priority.toLowerCase()] ?? "neutral"
    : "neutral"

  return (
    <ConfirmDialog
      isOpen={isOpen}
      onClose={onClose}
      title="Delete Prospect"
      // crm-api `leads#destroy` calls `soft_delete!` — it sets `deleted_at` and
      // nothing else, so the row simply falls out of `Customer`'s
      // `not_deleted` default scope. Don't describe this as permanent.
      description="Are you sure you want to delete this prospect? It is removed from the CRM lists, and the record itself is kept."
      confirmLabel="Delete Prospect"
      onConfirm={handleDelete}
    >
      <Box sx={{ bgcolor: "surfaceSoft", borderRadius: 1, p: 2, display: "flex", flexDirection: "column", gap: 1.5 }}>
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              {prospect.company_name || "Unknown Company"}
            </Typography>
            <Typography sx={{ color: "text.secondary" }}>{prospect.full_name}</Typography>
            <Typography variant="body2" sx={{ color: "text.disabled" }}>
              {prospect.email}
            </Typography>
          </Box>
          {prospect.priority && (
            <Chip
              label={`${prospect.priority} priority`}
              size="small"
              sx={{
                color: priorityTone === "neutral" ? "text.secondary" : `${priorityTone}.dark`,
                bgcolor: (t) =>
                  alpha(priorityTone === "neutral" ? t.palette.text.secondary : t.palette[priorityTone].main, 0.12),
              }}
            />
          )}
        </Box>

        {/* Financial wall: only show the deal value to a viewer with financial
            access (crm:financials); otherwise the whole line is dropped. */}
        {prospect.prospected_value && prospect.prospected_value > 0 ? (
          <FinancialOnly>
            <Typography variant="body2" sx={{ color: "text.secondary" }}>
              Expected Value: <Money value={prospect.prospected_value} options={{ decimals: 0 }} />
            </Typography>
          </FinancialOnly>
        ) : null}
      </Box>

      <Box sx={{ mt: 2, p: 2, border: 1, borderColor: (t) => alpha(t.palette.error.main, 0.3), borderRadius: 1, bgcolor: (t) => alpha(t.palette.error.main, 0.12) }}>
        <Typography variant="body2" sx={{ color: "error.dark" }}>
          <strong>What happens:</strong> the prospect is marked as deleted, so:
        </Typography>
        <Box component="ul" sx={{ color: "error.dark", mt: 1, pl: 2.5, listStyleType: "disc", "& li": { mb: 0.5 }, fontSize: "0.875rem" }}>
          <li>It disappears from the prospect lists, search and reports</li>
          <li>Its details, notes and activity history stop being reachable here</li>
          <li>Uploaded files and attachments are preserved, not erased</li>
          <li>The record is kept — an administrator can restore it</li>
        </Box>
      </Box>
    </ConfirmDialog>
  )
}
